/* The player. One position in time, not two tabs.
 *
 * ---------------------------------------------------------------------------
 * THE ONE IDEA
 *
 * Every product that does this -- security cameras, dashcams, a DVR, a hydro-
 * phone network like Orcasound -- eventually arrives at the same shape, and the
 * cleanest statement of it is the one live television found first: **there is
 * no mode, there is a position.** You are at the live edge, or you are somewhere
 * behind it. Pausing does not enter an archive; it moves you off the edge, and
 * one control brings you back.
 *
 * So this page has one play button, one set of filters and one clock, and the
 * only thing that changes when a farmer picks yesterday is where in time they
 * are standing. A Live tab and an Archive tab would have been two players to
 * build, two to learn, and two places for the filters to disagree.
 *
 * ---------------------------------------------------------------------------
 * THREE SURFACES, EACH ONE ALSO ITS OWN CONTROL
 *
 * This is what keeps two hydrophones and twenty-four hours on one uncluttered
 * screen without a settings panel:
 *
 *   the spectrum   is the filter -- the band is lit, the rest is dimmed, and
 *                  what you see removed is what you stop hearing
 *   the day strip  is the scrubber -- and it only takes a click where there is
 *                  actually something in storage to reach
 *   the time line  is the calendar -- it says where you are, and opens the day
 *                  picker when you press it
 *
 * ---------------------------------------------------------------------------
 * WHAT PRESSING PLAY ACTUALLY DOES
 *
 * Not "join a stream". There is no stream until somebody asks: the node is
 * told to start uploading, and then a segment arrives every second. Between the
 * press and the first sound is thirty seconds to three minutes, because there
 * is no command channel and the node finds out by polling.
 *
 * That wait is the hardest thing on this page and it is designed for rather
 * than hidden. It counts out loud, it says roughly how long, and it uses the
 * equaliser -- the existing idiom for "working" -- rather than acquiring the
 * spinner this repo has deliberately never had.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ApiError, api, klipp, qs, startLive, stopLive,
  type LiveInfo, type Me, type SiteClip, type SiteDetail, type StoredObject,
} from '../api'
import {
  CODEC_CEILING_HZ, FEED_BAND, FEED_MEANING, FISH_BAND, FISH_MEANING,
  INFRA_BAND, INFRA_MEANING,
} from '../bands'
import { DayStrip, type StripHour } from '../components/DayStrip'
import { Transport } from '../components/Transport'
import type { DayClip, DayHour } from '../components/DayTimeline'
import { Eq } from '../components/Loading'
import { Sensors, sensorName } from '../components/Sensors'
import { ListenAside } from '../components/ListenAside'
import { ClipShelf, SaveMark } from '../components/Klipp'
import { Spectrum, fmtHz } from '../components/Spectrum'
import { WelfareScale } from '../components/WelfareScale'
import { Waveform } from '../components/Waveform'
import { Kalender } from '../components/Telling'
import { ErrorBox, Rows, placeName } from '../components/bits'
import { PageHead } from '../components/Shell'
import { useLiveAudio, type Band } from '../live'
import { createSegmentPlayer, type Segment, type SegmentPlayer } from '../segments'
import { useT } from '../lang'
import {
  dayBounds, dayKey, dayLabel, setZone, shiftDay, stampFull, today, useZone,
  utcDaysFor, zoneLabel,
} from '../time'
import { useApi } from '../useApi'

/* Which band a listener is hearing, as a choice rather than as two numbers.
 *
 * Four, and two of them come with a warning the listener is shown rather than
 * spared. `Foringsautomat` is Phoniq's own figure and not a measurement this
 * system made, and it is wide enough to pass weather and shrimp as well as a
 * feeder. `Infralyd` cannot be heard at all -- 5-10 Hz is below human hearing
 * and below what any speaker reproduces -- so it is a band to watch on the
 * strip rather than to listen through. bands.ts carries the full argument for
 * both; this page carries the sentence. */
type PresetId = 'all' | 'fish' | 'feed' | 'infra' | 'custom'

/** The band, and the sentence that qualifies it. Null band means no filter. */
const PRESETS: Record<PresetId, { band: Band | null; meaning: string | null }> = {
  all: { band: null, meaning: null },
  infra: { band: INFRA_BAND, meaning: INFRA_MEANING },
  fish: { band: FISH_BAND, meaning: FISH_MEANING },
  feed: { band: FEED_BAND, meaning: FEED_MEANING },
  custom: { band: null, meaning: null },
}

/** Where in time the page is standing. */
type Pos =
  | { kind: 'now' }
  /** `day` is a date in the active zone; `at` is a playhead in epoch seconds. */
  | { kind: 'day'; day: string; at: number | null }

/** How the node splits its recordings, when nothing says otherwise. */
const FALLBACK_SEGMENT_S = 60

/** The transport's window inside a day. An hour is what the strip above hands
 *  you when you press it, and what somebody means by "twelve to half past". */
const HOUR_S = 3600

/** The longest span that can be kept as one clip.
 *
 *  The server enforces this and would refuse anything longer; it is repeated
 *  here so the button stops counting at the ceiling rather than letting
 *  somebody mark for seven hours and then be told no. Six hours is a whole
 *  feeding or a night, and somebody who means the day means the day -- which
 *  the strip already draws without a row. */
const MAX_MARK_S = 6 * 3600

interface DayDoc {
  hours: DayHour[]
  clips: DayClip[]
  segment_seconds: number
  uploads_audio: boolean
}

export function Listen({ siteId, me }: { siteId: string; me: Me }) {
  const t = useT()
  const zone = useZone()
  const site = useApi<SiteDetail>(`/v1/sites/${encodeURIComponent(siteId)}`)

  const active = useMemo(
    () => (site.data?.missions ?? [])
      .filter((m) => m.state === 'active')
      .sort((a, b) => a.started_at.localeCompare(b.started_at)),
    [site.data],
  )

  /* Which hydrophone.
   *
   * Defaulted to the first one in the water rather than the most recently
   * started. `active_mission_id` on the sites list is
   * `ORDER BY started_at DESC LIMIT 1`, and on the only two-sensor farm in the
   * fleet that picks the box holding no archive at all -- so every existing
   * link into audio opens the empty one. Oldest-first is not better in
   * principle; it is stable, which is what a default has to be. */
  const [sensor, setSensor] = useState<string | null>(null)
  const missionId = sensor ?? active[0]?.mission_id ?? null

  /** Whether this deployment's levels may be called decibels in the sea, and
   *  by how much to shift the analyser's dBFS if so. Both decided on the
   *  server; nothing here assembles a calibration out of parts. */
  const cal = active.find((m) => m.mission_id === missionId)?.calibration
  const offsetDb = cal?.absolute ? cal.offset_db ?? null : null
  /* Whether a term came off a datasheet rather than a bench. It does not
     change what is drawn, only how firmly -- and it is the server's word for
     it, not a guess assembled from which columns happen to be filled in. */
  const estimated = !!cal?.estimated

  const [pos, setPos] = useState<Pos>({ kind: 'now' })

  /** At the live edge, or behind it. The only mode this page has, and it is a
   *  position rather than a tab. */
  const live = pos.kind === 'now'

  /** Which day is drawn under the player. Today while live -- the strip is not
   *  a second screen you navigate to, it is the day you are already standing
   *  in, and being able to see it is what makes the archive discoverable at
   *  all. The first build hid it behind a text line reading "Nå CEST", and a
   *  farmer had no way to know there were eighteen hours under it. */
  const viewDay = pos.kind === 'day' ? pos.day : today()

  const [preset, setPreset] = useState<PresetId>('all')
  const [custom, setCustom] = useState<Band>({ lo: 100, hi: 2000 })
  const [showMeaning, setShowMeaning] = useState(false)
  const [pickingDay, setPickingDay] = useState(false)

  const band: Band | null =
    preset === 'custom' ? custom : PRESETS[preset].band

  /* ------------------------------------------------------------------ live */

  /* Which sensor on that hydrophone's node. '' is the hydrophone itself; a
   * node with a HydroMoth beside it offers 'moth' too. Each has its own
   * stream, and the node runs one at a time. Back to the hydrophone whenever
   * the node changes, because the next one may have nothing else. */
  const [stream, setStream] = useState('')
  useEffect(() => { setStream('') }, [missionId])
  const streamsInfo = useApi<LiveInfo>(
    live && missionId
      ? `/v1/missions/${encodeURIComponent(missionId)}/live?window=5`
      : null,
  )
  // The camera is watched from the node page. The server already leaves it
  // out for customers; staff see it listed and it is not a sound.
  const streams = (streamsInfo.data?.streams ?? []).filter((s) => s.kind !== 'camera')
  const sq = stream ? `&stream=${encodeURIComponent(stream)}` : ''

  const audio = useLiveAudio(pos.kind === 'now' ? missionId : null, stream)
  const [arming, setArming] = useState(false)
  const [armedAt, setArmedAt] = useState(0)
  const [waited, setWaited] = useState(0)
  const [armError, setArmError] = useState<string | null>(null)

  // Polled only while it matters: waiting for the node to converge, or already
  // playing. A page sitting idle on a phone in a pocket asks for nothing.
  const watching = pos.kind === 'now' && (arming || audio.status !== 'idle')
  const liveStatus = useApi<{ segments: number; live_service_desired: string }>(
    watching && missionId
      ? `/v1/missions/${encodeURIComponent(missionId)}/live?window=45${sq}`
      : null,
    { poll: 3000 },
  )
  const segments = liveStatus.data?.segments ?? 0

  const { start: startAudio, status: audioStatus, setBand: setLiveBand } = audio

  // The moment the first segment lands, play. Asking for a second press here
  // would be the difference between "it works" and "it works if you know".
  useEffect(() => {
    if (arming && segments > 0 && audioStatus === 'idle') {
      setArming(false)
      startAudio()
    }
  }, [arming, segments, audioStatus, startAudio])

  useEffect(() => {
    if (!arming) { setWaited(0); return }
    const id = window.setInterval(
      () => setWaited(Math.floor((Date.now() - armedAt) / 1000)), 500)
    return () => window.clearInterval(id)
  }, [arming, armedAt])

  useEffect(() => { setLiveBand(band) }, [band, setLiveBand])

  const goLive = async () => {
    if (!missionId || arming || audio.status !== 'idle') return
    setArmError(null)
    if (segments > 0) { audio.start(); return }
    setArming(true)
    setArmedAt(Date.now())
    setWaited(0)
    try {
      await startLive(missionId, stream)
      void liveStatus.reload()
    } catch (e) {
      setArming(false)
      setArmError(e instanceof ApiError ? e.detail ?? e.code : String(e))
    }
  }

  /* Stopping matters as much as starting. A node left streaming uploads a
   * segment a second to nobody over a farm uplink until its own half-hour
   * limit cuts it off. */
  const stopLiveAll = useCallback(async (id: string | null) => {
    audio.stop()
    setArming(false)
    if (!id) return
    try { await stopLive(id) } catch { /* the sound has already stopped */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audio.stop])

  /* ------------------------------------------------------------- the archive
   *
   * A local day straddles two UTC days, and the endpoint buckets by UTC. So
   * both are fetched and merged, and the result is filtered to the day the
   * listener actually means. Two requests, no backend change, and a farmer who
   * picks "yesterday" gets a strip that starts at midnight where they are
   * standing rather than at 02:00.
   *
   * Fetched whether or not the listener has gone looking. The strip is on the
   * page from the first paint, so this is the load that tells them there is
   * anything to press. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dayKeys = useMemo(() => utcDaysFor(viewDay), [viewDay, zone])
  const dayA = useApi<DayDoc>(
    missionId && dayKeys[0]
      ? `/v1/missions/${encodeURIComponent(missionId)}/day?date=${dayKeys[0]}`
      : null,
  )
  const dayB = useApi<DayDoc>(
    missionId && dayKeys[1]
      ? `/v1/missions/${encodeURIComponent(missionId)}/day?date=${dayKeys[1]}`
      : null,
  )

  const segmentS =
    dayA.data?.segment_seconds ?? dayB.data?.segment_seconds ?? FALLBACK_SEGMENT_S

  const { hours, clips } = useMemo(() => {
    const [t0, t1] = dayBounds(viewDay)
    const out: StripHour[] = []
    const seen: DayClip[] = []
    dayKeys.forEach((key, i) => {
      const doc = i === 0 ? dayA.data : dayB.data
      if (!doc) return
      // The UTC midnight this document is about, which is what its `h` counts
      // from. Built in UTC deliberately: the document is a UTC day whatever
      // clock the page is showing.
      const [y, m, d] = key.split('-').map(Number)
      const base = Date.UTC(y!, m! - 1, d!) / 1000
      for (const h of doc.hours) {
        const at = base + h.h * 3600
        if (at < t0 || at >= t1) continue
        out.push({ t: at, cloud_seconds: h.cloud_seconds, node_seconds: h.node_seconds })
      }
      for (const c of doc.clips) {
        const at = new Date(c.started_utc).getTime() / 1000
        if (at < t0 || at >= t1) continue
        if (!seen.some((x) => x.clip_id === c.clip_id)) seen.push(c)
      }
    })
    out.sort((a, b) => a.t - b.t)
    return { hours: out, clips: seen }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewDay, dayKeys, dayA.data, dayB.data, zone])

  /** How much of this day is in storage and therefore pressable. Said as a
   *  number under the strip, because a bar chart of green columns is a picture
   *  and "18 t lyd" is a fact. */
  const storedS = useMemo(
    () => hours.reduce((n, h) => n + h.cloud_seconds, 0), [hours])

  /* The archive player.
   *
   * One `createSegmentPlayer` for whatever span is being listened to -- a day
   * or a single clip. A clip is a segment with its own duration, so both go
   * through the same object and inherit the same normalisation, the same
   * filters and the same gap handling. */
  /* One AudioContext for the whole page, opened by the first press.
   *
   * Not an optimisation. A browser grants an AudioContext only from inside a
   * user gesture, and the archive path is: press the strip -> fetch the day's
   * objects -> build a player. The fetch ends the gesture, so a context created
   * at the far end of it is born suspended and refuses to resume -- silence,
   * with the clock still running, which is the hardest failure on this page to
   * notice. live.ts has the long version of this lesson in its own comments;
   * this is the same lesson applied to the other half of the player.
   *
   * So it is opened synchronously in the handler, before anything is awaited,
   * and lent to each span. One rather than one-per-span because browsers cap
   * concurrent contexts at around six and scrubbing would exhaust that in a
   * couple of minutes. */
  const ctxRef = useRef<AudioContext | null>(null)
  const openContext = useCallback(() => {
    if (!ctxRef.current) ctxRef.current = new AudioContext()
    // Resumed here too: on iOS a context is created running only if the gesture
    // is still on the stack, and resuming one that is already running is free.
    void ctxRef.current.resume().catch(() => { /* reported by the player */ })
    return ctxRef.current
  }, [])
  useEffect(() => () => { void ctxRef.current?.close().catch(() => {}) }, [])

  const player = useRef<SegmentPlayer | null>(null)
  const [archiveAnalyser, setArchiveAnalyser] = useState<AnalyserNode | null>(null)
  const [span, setSpan] = useState<{ segments: Segment[]; at: number } | null>(null)
  const [playing, setPlaying] = useState(false)
  const [at, setAt] = useState<number | null>(null)
  const lastAt = useRef(0)
  const [lift, setLift] = useState(0)
  const [archiveError, setArchiveError] = useState<string | null>(null)

  /** The span the player is framed on, when it is framed on one.
   *
   *  A clip -- kept or being kept -- is its own length, and the transport
   *  should say so rather than showing the hour it happens to fall in. Null
   *  means "somewhere in a day", which is the ordinary case and the one the
   *  hour window is for. */
  const [focus, setFocus] = useState<
    { t0: number; t1: number; clipId: string | null } | null>(null)
  const bandRef = useRef(band)
  bandRef.current = band

  useEffect(() => {
    player.current?.destroy()
    player.current = null
    setArchiveAnalyser(null)
    setPlaying(false)
    if (!span || !missionId) return
    const p = createSegmentPlayer({
      missionId,
      segments: span.segments,
      band: bandRef.current,
      context: ctxRef.current ?? undefined,
      /* Throttled to about ten a second.
       *
       * `onTime` fires on every animation frame, and every one of them used to
       * set page state -- so a page holding the sensor chips, a spectrum, a
       * 24-hour strip and now a transport re-rendered sixty times a second for
       * the whole of playback. A tenth of a second moves the transport knob by
       * three pixels on a phone and the day strip playhead by none at all,
       * which is to say the other fifty renders drew nothing. */
      onTime: (e) => {
        if (Math.abs(e - lastAt.current) < 0.1) return
        lastAt.current = e
        setAt(e)
      },
      onGain: setLift,
      onAnalyser: setArchiveAnalyser,
      onState: (s) => setPlaying(s === 'playing'),
    })
    player.current = p
    p.seek(span.at)
    void p.play()
    return () => { p.destroy() }
  }, [span, missionId])

  useEffect(() => { player.current?.setBand(band) }, [band])

  /* Everything stored for this day, as segments the player can schedule.
   *
   * Paginated, and it has to be. The object index caps a page at 500 and
   * orders newest-first, while a day of 60-second segments is 1440 of them --
   * so a single request covers the last eight hours and nothing else. Ask for
   * the morning and every hour before about 16:00 would have come back empty
   * and played as silence, which is exactly the failure this player is built
   * to make impossible: a gap and an unfetched page are indistinguishable once
   * they reach the speaker.
   *
   * Capped anyway. Two days of one-second segments would be 172 800 objects
   * and that is a page nobody should be able to ask this browser to hold; the
   * cap is far above a normal day and exists so a misconfigured node cannot
   * take the tab down. */
  const loadDaySegments = useCallback(async (
    day: string, forMission?: string,
  ): Promise<Segment[]> => {
    // Named explicitly by the clip opener, which may be reaching for audio
    // recorded by the OTHER hydrophone at this farm -- and `sensor` will not
    // have caught up in the same tick that asks for it.
    const mid = forMission ?? missionId
    if (!mid) return []
    const [t0, t1] = dayBounds(day)
    const found: StoredObject[] = []
    const MAX_OBJECTS = 4000
    // Both kinds. The proxy is preferred below, exactly as SelectionPlayer
    // prefers it: an hour of 24-bit FLAC is ~512 MB and about 49 minutes over a
    // farm uplink; the same hour at 64 kbit is ~30 MB and under three.
    for (const kind of ['reduced', 'audio']) {
      let cursor: string | null = null
      for (;;) {
        const r: { items: StoredObject[]; next_cursor: string | null } =
          await api.get(
            `/v1/missions/${encodeURIComponent(mid)}/objects`
            + qs({
              kind, state: 'stored', limit: 500, cursor,
              since: new Date(t0 * 1000).toISOString(),
              until: new Date(t1 * 1000).toISOString(),
            }))
        found.push(...r.items)
        cursor = r.next_cursor
        if (!cursor || found.length >= MAX_OBJECTS) break
      }
    }
    const proxies = found.filter((o) => o.kind === 'reduced')
    const pick = proxies.length ? proxies : found
    return pick
      .filter((o) => o.start_utc)
      .map((o) => ({
        key: o.key,
        startUtc: new Date(o.start_utc!).getTime() / 1000,
        durationS: segmentS,
        size: o.size,
        kind: o.kind,
      }))
      .sort((a, b) => a.startUtc - b.startUtc)
  }, [missionId, segmentS])

  /** Press a point in the day.
   *
   *  Pressable from the live edge as well, and that is the whole DVR idea: you
   *  do not switch to an archive, you move back along the day you are already
   *  looking at. The live uplink is stopped on the way past -- a node left
   *  sending a segment a second to nobody is the one cost of this page. */
  const seekArchive = async (epoch: number) => {
    // Before the fetch, while the gesture is still on the stack.
    openContext()
    setArchiveError(null)
    // Pressing the day is stepping out of whatever clip was framed. Leaving
    // `focus` set would keep the transport spanning a clip the playhead has
    // already left, and would pause playback the moment it passed that clip's
    // end -- in the middle of an ordinary scrub.
    setFocus(null)
    const day = viewDay
    if (live) {
      void stopLiveAll(missionId)
      setPos({ kind: 'day', day, at: null })
    }
    if (!live && span && !span.segments.some((s) => s.kind === 'clips')) {
      player.current?.seek(epoch)
      setAt(epoch)
      void player.current?.play()
      return
    }
    try {
      const segs = await loadDaySegments(day)
      if (!segs.length) {
        setArchiveError(t('Nothing from this day has reached us yet.'))
        return
      }
      /* Land on audio, not on the hour mark.
       *
       * An hour is drawn as a proportion, so an hour holding 46 minutes that
       * begin at :14 is 77% full and reachable -- and seeking to the top of it
       * plays fourteen minutes of silence first. Gaps inside a span are
       * deliberately silent (rule 4), which makes an accidental one
       * indistinguishable from a real one. So the playhead moves forward to
       * the next segment that actually exists. */
      const covering = segs.find(
        (sg) => sg.startUtc <= epoch && epoch < sg.startUtc + sg.durationS)
      const next = covering ?? segs.find((sg) => sg.startUtc >= epoch)
      setSpan({ segments: segs, at: next ? Math.max(epoch, next.startUtc) : epoch })
    } catch (e) {
      setArchiveError(e instanceof ApiError ? e.detail ?? e.code : String(e))
    }
  }

  /* A mark drawn on the strip and the same mark on the shelf must open the
   * same way, so the strip's handler resolves the row it is holding to the
   * full record and hands it to one opener. /day carries only what the strip
   * needs to DRAW a clip; `kept` is what is needed to PLAY one, and a mark
   * has no `key` for the old single-object path to use at all.
   *
   * The fallback is not dead code: /day is per mission and answers instantly,
   * while the site-wide list is one more request that may still be in flight
   * on the first paint. A live clip can be opened without it. */
  const openClip = (c: DayClip) => {
    const full = kept.data?.items.find((k) => k.clip_id === c.clip_id)
    if (full) { void openKept(full); return }
    if (!c.key) return
    openContext()
    const start = new Date(c.started_utc).getTime() / 1000
    setArchiveError(null)
    if (live) {
      void stopLiveAll(missionId)
      setPos({ kind: 'day', day: viewDay, at: null })
    }
    setFocus({ t0: start, t1: start + c.duration_s, clipId: c.clip_id })
    setSpan({
      segments: [{
        key: c.key, startUtc: start, durationS: c.duration_s,
        size: c.size, kind: 'clips',
      }],
      at: start,
    })
  }

  /* ---------------------------------------------------------------- klipp
   *
   * What this farm has kept, and the one control that keeps another.
   *
   * Fetched by SITE and not by day, which is what makes the shelf an album
   * instead of a second drawing of the strip: the strip is one day wide, and
   * the clip somebody actually wants is usually the one from three weeks ago
   * that they half remember. Pressing one moves the page to its day.
   *
   * Every colleague scoped to this farm reads the same list, so a clip is
   * shared the moment it is saved. There is no share step and no private
   * state -- see the header of Klipp.tsx for why that is the design rather
   * than a shortcut. */
  // Not for a device: the receiver refuses it the clip list, and that 401
  // would sign the tablet out (see Home for the same trap).
  const kept = useApi<{ items: SiteClip[] }>(me.via === 'device' ? null
    : `/v1/sites/${encodeURIComponent(siteId)}/clips`)

  /** Where the current mark began, or null when not marking.
   *
   *  Two presses rather than a dragged selection, and that is the whole reason
   *  this works on a phone. The day strip is twenty-four hours across three
   *  hundred pixels and the transport is an hour across the same three hundred
   *  -- brushing a range on either one with a thumb, on a boat, is a precision
   *  task. Pressing once when you hear it and once when it stops is not, and
   *  it is the control this page already uses for live recording, so there is
   *  one idiom instead of two. */
  const [marking, setMarking] = useState<number | null>(null)
  /** A finished span, waiting for its note. */
  const [pending, setPending] = useState<{ t0: number; t1: number } | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const startMark = () => {
    if (at == null) return
    setSaveError(null)
    setMarking(at)
  }

  const stopMark = () => {
    if (marking == null || at == null) return
    // Whichever way round they pressed. Somebody who hears something, seeks
    // BACK to where it started and presses again has described the same span,
    // and refusing that would be a rule about our state machine rather than
    // about their intention.
    const t0 = Math.min(marking, at)
    const t1 = Math.max(marking, at)
    setMarking(null)
    // A press and an immediate second press is a mis-tap, not a clip.
    if (t1 - t0 < 1) return
    setPending({ t0, t1: Math.min(t1, t0 + MAX_MARK_S) })
  }

  const saveMark = async (note: string) => {
    if (!pending || !missionId) return
    setSaving(true)
    setSaveError(null)
    try {
      await klipp.mark(siteId, {
        mission_id: missionId,
        started_utc: new Date(pending.t0 * 1000).toISOString(),
        duration_s: Math.round((pending.t1 - pending.t0) * 100) / 100,
        note: note.trim() || null,
      })
      setPending(null)
      void kept.reload()
      // And the day documents, so the new mark appears on the strip where it
      // was made rather than only after a reload. The strip reads /day, which
      // does not know about the request that just returned.
      void dayA.reload()
      void dayB.reload()
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.detail ?? e.code : String(e))
    } finally {
      setSaving(false)
    }
  }

  /** Put a kept clip in the player, wherever and whenever it came from.
   *
   *  Three things can be wrong with where the page is standing when somebody
   *  presses a clip on the shelf: the wrong day, the wrong hydrophone, and the
   *  live edge. All three are corrected here, because a list that can only
   *  open the clips from the day you already chose is not an album. */
  const openKept = async (c: SiteClip) => {
    // Before anything is awaited, while the gesture is still on the stack --
    // the lesson in openContext's own comment.
    openContext()
    setArchiveError(null)
    setMarking(null)
    setPending(null)
    const start = new Date(c.started_utc).getTime() / 1000
    const day = dayKey(start)
    if (live) void stopLiveAll(missionId)
    if (c.mission_id !== missionId) setSensor(c.mission_id)
    setPos({ kind: 'day', day, at: start })
    setFocus({ t0: start, t1: start + c.duration_s, clipId: c.clip_id })

    // A live clip is one object: fetched whole and decoded whole, which is
    // what ClipPlayer and the strip's own clip marks have always done.
    if (c.key) {
      setSpan({
        segments: [{
          key: c.key, startUtc: start, durationS: c.duration_s,
          size: c.size, kind: 'clips',
        }],
        at: start,
      })
      return
    }

    /* A mark has no object. Its audio is the day's own segments, narrowed to
     * the ones the span touches -- so the player stops when it runs out of
     * them instead of playing on into the rest of the afternoon. */
    try {
      const segs = await loadDaySegments(day, c.mission_id)
      const inSpan = segs.filter(
        (sg) => sg.startUtc + sg.durationS > start
          && sg.startUtc < start + c.duration_s)
      if (!inSpan.length) {
        // Said plainly. A mark points at audio rather than owning a copy of
        // it, and if that audio has gone the honest answer is that it has
        // gone -- not a player that loads and produces silence.
        setArchiveError(t('That recording is no longer in storage.'))
        return
      }
      setSpan({ segments: inSpan, at: start })
    } catch (e) {
      setArchiveError(e instanceof ApiError ? e.detail ?? e.code : String(e))
    }
  }

  /* Stop where the clip stops.
   *
   * The segment player has no end bound: it plays the row of objects it was
   * handed, and objects are whole minutes. A forty-second mark would therefore
   * run most of a minute past its own end. Watched here rather than added to
   * segments.ts, because where a clip ends is a fact about the clip and not
   * about the row of segments underneath it. */
  useEffect(() => {
    if (!focus || at == null || !playing) return
    if (at >= focus.t1) player.current?.pause()
  }, [at, focus, playing])

  /* A clip somebody sent, opened on arrival.
   *
   * `?klipp=clp-...` on the page that already exists, rather than a route of
   * its own -- which is what lets a colleague paste a link into a message
   * without this feature costing a slot in a mobile tab bar that has none
   * left. Runs once per id: the query is stripped afterwards so that pressing
   * something else and then reloading does not drag the page back.
   */
  const openedFromLink = useRef<string | null>(null)
  useEffect(() => {
    const want = new URLSearchParams(window.location.search).get('klipp')
    if (!want || openedFromLink.current === want) return
    const found = kept.data?.items.find((c) => c.clip_id === want)
    if (!found) return
    openedFromLink.current = want
    void openKept(found)
    window.history.replaceState({}, '', window.location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kept.data])

  /* A moment somebody linked to, opened on arrival.
   *
   * `?tid=<epoch seconds>` on the same page, for the same reason `?klipp`
   * is: a published report says "this happened at 19:49" and the reader
   * should be able to hear it without being told to scrub for it.
   *
   * A timestamp and not a clip. Nothing has to be saved in advance, nothing
   * expires, and a link stays meaningful for as long as the recording does --
   * which is exactly as long as it should. If the audio has been evicted the
   * ordinary archive path says so in its own words, and no special case is
   * needed here to produce that sentence.
   *
   * Runs once per value, and strips the query afterwards, so that pressing
   * somewhere else and then reloading does not drag the page back to it.
   */
  const openedMoment = useRef<number | null>(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('tid')
    if (!raw) return
    const epoch = Number(raw)
    // A finite epoch inside the era this product can have recorded in. A
    // hostile or fat-fingered value must not send the page hunting for a day
    // in 1970 and then show an error it cannot explain.
    if (!Number.isFinite(epoch) || epoch < 1_600_000_000
        || epoch > Date.now() / 1000 + 86_400) return
    if (openedMoment.current === epoch) return
    /* Not until the page knows which hydrophones this site has.
     *
     * `active` is empty on the first render, and `missionId` is null with it.
     * Claiming the link there resolves `?hydrofon` against an empty list, so
     * the sensor the report was made on silently becomes "whichever loads
     * first" -- and since claiming also strips the query, the mistake cannot
     * be retried on the render where the answer arrives. It looked exactly
     * like a day with no recording, because that is the branch it fell into.
     */
    if (!active.length) return
    openedMoment.current = epoch
    window.history.replaceState({}, '', window.location.pathname)
    void (async () => {
      openContext()
      setArchiveError(null)
      setMarking(null)
      setFocus(null)
      const day = dayKey(epoch)
      /* Which hydrophone, before which day.
       *
       * A site can carry two, and they do not hear the same thing. Left to
       * itself the page opens on the first one it finds, and asking that one
       * for a day it never recorded produces "nothing from this day has
       * reached us yet" -- which is true of that sensor and wrong about the
       * link. `sensor` state has not settled inside this tick, so the mission
       * is also passed to `loadDaySegments` explicitly, exactly as the clip
       * opener does for the same reason. */
      const want = params.get('hydrofon')
      const onMission = want && active.some((m) => m.mission_id === want)
        ? want : missionId
      if (!onMission) {
        // Cannot happen with the guard above, and said out loud rather than
        // left to `loadDaySegments`, which answers an empty list for "no
        // mission" and for "no audio" alike.
        setArchiveError(t('No listening post is in the water at this farm.'))
        return
      }
      if (onMission !== missionId) setSensor(onMission)
      if (live) void stopLiveAll(missionId)
      setPos({ kind: 'day', day, at: epoch })
      try {
        const segs = await loadDaySegments(day, onMission)
        if (!segs.length) {
          setArchiveError(t('Nothing from this day has reached us yet.'))
          return
        }
        /* Land on audio, not on the mark -- the same rule seekArchive
         * follows, and for the same reason: a gap is deliberately silent, so
         * seeking into one is indistinguishable from a fault. */
        const covering = segs.find(
          (sg) => sg.startUtc <= epoch && epoch < sg.startUtc + sg.durationS)
        const next = covering ?? segs.find((sg) => sg.startUtc >= epoch)
        if (!next) {
          setArchiveError(t('That recording is no longer in storage.'))
          return
        }
        setSpan({ segments: segs, at: Math.max(epoch, next.startUtc) })
      } catch (e) {
        setArchiveError(e instanceof ApiError ? e.detail ?? e.code : String(e))
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId, active])

  /* --------------------------------------------------------------- moving */

  /** Switching sensor keeps the position in time. Two hydrophones at one farm
   *  exist so a sound can be compared between them, and a switch that jumped
   *  back to now would make that comparison impossible to perform. */
  const pickSensor = (next: string) => {
    if (next === missionId) return
    void stopLiveAll(missionId)
    setSpan(null)
    setFocus(null)
    setMarking(null)
    setSensor(next)
  }

  /** Hydrophone or HydroMoth. While sounding, the switch carries on
   *  listening: the server starts the one and stops the other in a single
   *  revision, and the new stream plays as soon as its first segment lands. */
  const pickStream = async (next: string) => {
    if (next === stream || !missionId) return
    const wasOn = arming || audio.status !== 'idle'
    audio.stop()
    setStream(next)
    if (!wasOn) return
    setArmError(null)
    setArming(true)
    setArmedAt(Date.now())
    setWaited(0)
    try {
      await startLive(missionId, next)
    } catch (e) {
      setArming(false)
      setArmError(e instanceof ApiError ? e.detail ?? e.code : String(e))
    }
  }

  const pickDay = (day: string) => {
    setPickingDay(false)
    void stopLiveAll(missionId)
    setSpan(null)
    setFocus(null)
    setMarking(null)
    setAt(null)
    setPos({ kind: 'day', day, at: null })
  }

  const backToNow = () => {
    setSpan(null)
    setFocus(null)
    setMarking(null)
    setAt(null)
    setPos({ kind: 'now' })
  }

  /* Where "now" is on the strip while the page is at the live edge.
   *
   * The playhead sits at the right-hand end of what exists and creeps with the
   * clock, which is what makes "you are at the edge, and you can walk back"
   * legible without a word of explanation. Half a minute is fine: the strip is
   * twenty-four hours wide, so a tick moves it by a third of a pixel. */
  const [nowTick, setNowTick] = useState(() => Date.now() / 1000)
  useEffect(() => {
    if (!live) return
    setNowTick(Date.now() / 1000)
    const id = window.setInterval(() => setNowTick(Date.now() / 1000), 30_000)
    return () => window.clearInterval(id)
  }, [live])

  /* ---------------------------------------------------------------- render */

  if (site.error) return <ErrorBox error={site.error} />
  if (!site.data) return <Rows n={2} />

  if (!active.length) {
    return (
      <>
        <PageHead title={site.data.name}
          lead={placeName(site.data.municipality) ?? undefined} />
        <p className="empty">{t('No listening post is in the water at this farm.')}</p>
      </>
    )
  }

  const busy = arming || audio.status === 'starting'
  const sounding = live
    ? audio.status === 'playing' || audio.status === 'stalled'
    : playing

  /* What the transport spans: the thing you are inside.
   *
   * A clip is its own length. Inside a day, it is the hour you landed in --
   * which is the unit the strip above hands you, and the unit somebody means
   * when they say "twelve to half past". Derived from the playhead rather than
   * from where you clicked, so playing out of one hour and into the next moves
   * the window with it. */
  const clipSeg = span && span.segments.length === 1
    && span.segments[0]!.kind === 'clips' ? span.segments[0]! : null
  const winStart = at == null ? 0 : Math.floor(at / HOUR_S) * HOUR_S
  /* `focus` first, because it is the only one of the three that knows about a
     mark: a mark's segments are ordinary minutes of the day, so `clipSeg`
     cannot recognise one and the window would silently fall back to the hour
     -- drawing a forty-second clip as a knob that never leaves the left edge. */
  const win: [number, number] | null = !live && span && at != null
    ? (focus
        ? [focus.t0, focus.t1]
        : clipSeg
          ? [clipSeg.startUtc, clipSeg.startUtc + clipSeg.durationS]
          : [winStart, winStart + HOUR_S])
    : null

  /** Move inside what is already loaded. No fetch, no state change beyond the
   *  playhead -- and it lands on audio rather than in a gap, for the reason the
   *  day strip refuses an empty hour. */
  const seekWithin = (epoch: number) => {
    if (!player.current || !span) return
    const segs = span.segments
    const covering = segs.find(
      (sg) => sg.startUtc <= epoch && epoch < sg.startUtc + sg.durationS)
    const next = covering ?? segs.find((sg) => sg.startUtc >= epoch)
    const target = covering ? epoch : (next ? next.startUtc : epoch)
    player.current.seek(target)
    lastAt.current = target
    setAt(target)
  }

  const press = () => {
    openContext()
    if (live) {
      if (busy) return
      if (sounding) { void stopLiveAll(missionId); return }
      void goLive()
      return
    }
    if (playing) { player.current?.pause(); return }
    if (player.current) {
      /* At the end of a clip, play means play it again.
       *
       * A framed clip is paused by the effect above the moment the playhead
       * reaches its end. Without this, the next press hands the playhead
       * straight back to that effect and it pauses again -- a play button that
       * visibly does nothing, on the one screen where somebody is most likely
       * to want to hear the same four seconds twice. */
      if (focus && at != null && at >= focus.t1 - 0.25) {
        player.current.seek(focus.t0)
        lastAt.current = focus.t0
        setAt(focus.t0)
      }
      void player.current.play()
      return
    }
    // Nothing loaded yet: start at the first hour that has anything in it,
    // rather than at midnight, which on most days is a gap.
    const first = hours.find((h) => h.cloud_seconds > 0)
    if (first) void seekArchive(first.t)
    else setArchiveError(t('Nothing from this day has reached us yet.'))
  }

  const label = live
    ? (busy ? t('Waking the listening post') : sounding ? t('Stop') : t('Listen now'))
    : (playing ? t('Pause') : t('Play'))

  const todayKey = today()
  const isToday = viewDay === todayKey

  return (
    <div className="listen">
      <PageHead
        tight
        title={site.data.name}
        lead={placeName(site.data.municipality) ?? undefined}
      />

      <Sensors
        missions={active}
        selected={missionId ?? ''}
        onSelect={pickSensor}
      />

      {live && streams.length > 1 && (
        <div className="seg sm listen-streams" role="tablist"
             aria-label={t('Sensor')}>
          {streams.map((s) => (
            <button key={s.id} type="button" role="tab"
                    aria-selected={s.id === stream}
                    disabled={s.present === false}
                    title={s.present === false ? t('Not connected') : undefined}
                    onClick={() => { void pickStream(s.id) }}>
              {s.primary ? t('Hydrophone') : streamLabel(s.sensor, s.id)}
            </button>
          ))}
        </div>
      )}

      <div className="listen-main">

      {/* The player, boxed.
       *
       * Not decoration. Floating the button, the spectrum and the filter row
       * loose on a 1280px page made five unrelated things at five widths, and
       * the whole read was "engineering dashboard" -- which is exactly what
       * this page is not for. One surface, one column, one width. */}
      <section className="listen-card">
        <div className="player">
          {/* The button stands in the middle of its own signal. The canvas is
              absolutely placed across the card and the button sits on top of
              it, so the two share a centre without either one being nested in
              the other's layout. */}
          <div className="play-stage">
            <Waveform analyser={live ? audio.analyser : archiveAnalyser} />
          <button
            type="button"
            /* `waking` is the wait for the node, which is thirty seconds to
               three minutes and is the hardest thing on this page. It gets
               motion of its own rather than a static circle. */
            className={`play${sounding ? ' on' : ''}${busy ? ' waking' : ''}`}
            onClick={press}
            aria-label={label}
          >
            {busy ? <Eq label={t('Waking the listening post')} />
              : sounding ? <IconStop /> : <IconPlay />}
          </button>
          </div>

          {/* Two lines, and the split is deliberate. The first says what state
              you are in, at a size somebody reads across a boat deck; the second
              carries the qualification, the clock and the count. The first build
              crammed all of it into one 15px line and the most important word on
              the page was the same weight as the caveat beside it. */}
          <p className="play-say">
            {live && busy && t('Waking the listening post')}
            {live && !busy && sounding && (
              <>
                <span className="pulse on" aria-hidden="true" />
                {' '}{t('listening now')}
              </>
            )}
            {live && !busy && !sounding && audio.status === 'ended'
              && t('Listen again')}
            {live && !busy && !sounding && audio.status !== 'ended' && t('Listen now')}
            {/* Without seconds. At headline size a digit changing once a
                second is the busiest thing on the page, and the strip under it
                is twenty-four hours wide -- there is no question here that a
                second answers. */}
            {!live && at != null && stampFull(at, false)}
            {!live && at == null && dayLabel(dayBounds(viewDay)[0])}
          </p>

          <p className="play-sub">
            {live && busy && (
              <>
                <span className="num">{waited}s</span>
                {' · '}{t('usually under two minutes')}
              </>
            )}
            {live && !busy && sounding && t('straight from the listening post')}
            {live && !busy && !sounding && audio.status === 'ended'
              && t('The listening post stopped sending after 30 minutes.')}
            {live && !busy && !sounding && audio.status !== 'ended'
              && t('straight from the listening post')}
            {!live && at == null && t('Press the day below to start')}
            {!live && at != null && (playing ? t('from the recording') : t('Paused'))}
          </p>

          {/* The way back to the edge, beside the button that took you off it
              rather than filed away at the bottom of the page. */}
          {/* Outlined rather than ghost. This is the way back to the live edge
              and the only navigation inside the player; a borderless label is
              what everything else secondary on the page looks like. */}
          {!live && (
            <button type="button" className="btn sm" onClick={backToNow}>
              {t('Back to now')}
            </button>
          )}
        </div>

        {/* The transport, under the button where a player keeps it. Only when
            something is loaded: a scrub bar over nothing is a control that
            cannot be wrong because it cannot do anything. */}
        {win && at != null && span && (
          <Transport
            t0={win[0]} t1={win[1]} at={at}
            segments={span.segments}
            onSeek={seekWithin}
          />
        )}

          {audio.error && <p className="notice bad">{audio.error}</p>}
          {armError && <p className="notice bad">{armError}</p>}
          {archiveError && <p className="notice bad">{archiveError}</p>}

          {/* Said once, and only when the measurement says it is true. On older
              WebKit a natively played stream gives the analyser nothing, and
              because capturing the element has already routed it away from the
              speakers, that is silence rather than a dark picture. The fallback
              restores the sound and loses the graph -- so the controls that
              depend on the graph are gone from the page rather than present and
              doing nothing. */}
          {live && audio.graphless && (
            <p className="notice">
              {t('This browser cannot analyse a live stream, so the filters are '
                + 'not available here. The sound is unchanged, and yesterday '
                + 'still filters normally.')}
            </p>
          )}

          {/* iOS mutes web audio with the hardware switch, and it is the most
              common "no sound" call there is. Only shown once there is genuinely
              something to hear, so it never appears as an excuse for a stream
              that is not running. */}
          {sounding && isApple() && (
            <p className="faint play-hint">
              {t('Hear nothing? Check the silent switch on the side of the phone.')}
            </p>
          )}

        {(!live || !audio.graphless) && (
          <>
            <Spectrum
              analyser={live ? audio.analyser : archiveAnalyser}
              band={band}
              onBand={(b) => { setPreset('custom'); setCustom(b) }}
              editable={preset === 'custom'}
              sampleRate={48000}
              offsetDb={offsetDb}
            />

            {/* Under the strip, because it reads as a consequence of it: that
                is the sound, this is how much of it. Absent entirely on an
                uncalibrated mission -- see the component header. */}
            <WelfareScale
              analyser={live ? audio.analyser : archiveAnalyser}
              offsetDb={offsetDb}
              estimated={estimated}
            />

            <div className="presetrow">
              {/* A band-pass response: skirts either side of a flat top, which
                  is literally what the two biquads underneath are doing. It
                  labels the row without a word, in the one place on this page
                  where an icon can say something a Norwegian and an English
                  reader both already know. */}
              <span className="presets-icon" aria-hidden="true"><IconFilter /></span>
              <div className="presets" role="tablist" aria-label={t('Filter')}>
                {([
                  ['all', t('All')],
                  ['infra', t('Infrasound')],
                  ['fish', t('The fish')],
                  ['feed', t('Feeder')],
                  ['custom', t('Own')],
                ] as [PresetId, string][]).map(([id, name]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={preset === id}
                    className="preset"
                    onClick={() => setPreset(id)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            {/* The range is the button. A preset that claims to be the fish's
                ear -- or a feeder, or a frequency nobody can hear -- owes the
                reader the strength of that claim, and the range is the thing
                they came to check. */}
            {band && preset !== 'custom' && (
              <p className="preset-say">
                <button type="button" className="linkish"
                  aria-expanded={showMeaning}
                  onClick={() => setShowMeaning(!showMeaning)}>
                  {fmtHz(band.lo)}–{fmtHz(band.hi)}
                </button>
                {band.hi > CODEC_CEILING_HZ && (
                  <span className="faint"> · {t('the top is not in this stream')}</span>
                )}
                {showMeaning && (
                  <span className="faint"> {PRESETS[preset].meaning}</span>
                )}
              </p>
            )}
            {preset === 'infra' && (
              /* Said without needing to be opened. A control that produces
                 silence on every device a farm owns must say so where it is
                 pressed, not behind a disclosure. */
              <p className="preset-say faint">{t('Below hearing — watch the strip.')}</p>
            )}
            {preset === 'custom' && (
              <p className="preset-say faint">
                {fmtHz(custom.lo)}–{fmtHz(custom.hi)}
                {' · '}
                {t('drag the edges')}
              </p>
            )}
          </>
        )}

        {/* Recording is secondary and looks it. Only while live, only where the
            graph exists to tap, and only if this farm bought clips. */}
        {live && sounding && !audio.graphless && me.can?.clip !== false && (
          <div className="play-more">
            <button
              type="button"
              className={audio.recording ? 'btn danger sm' : 'btn ghost sm'}
              onClick={() => (audio.recording
                ? audio.stopRecording() : audio.startRecording())}
            >
              {audio.recording
                ? `${t('Stop and save')} · ${audio.recordedSeconds}s`
                : t('Record sound')}
            </button>
            {audio.clipState === 'saved' && (
              <span className="faint">{t('Saved in recordings')}</span>
            )}
            {audio.clipState === 'failed' && (
              <span className="faint">{t('Saved on this device only.')}</span>
            )}
          </div>
        )}

        {/* The same act, on the other side of the live edge.
            
            Deliberately the same control in the same place with the same two
            words, because it is the same thing a person is doing: pressing
            once when they hear something and once when it stops. The only
            difference is what happens underneath -- live encodes and uploads,
            here nothing moves at all -- and that is not a difference anybody
            pressing the button has to know about.
            
            Only once there is a playhead: marking needs somewhere to start
            from, and a button that silently does nothing until you have
            pressed play is a button that teaches people it is broken. */}
        {!live && at != null && span && me.can?.clip !== false && !pending && (
          <div className="play-more">
            <button
              type="button"
              className={marking != null ? 'btn danger sm' : 'btn ghost sm'}
              onClick={marking != null ? stopMark : startMark}
            >
              {marking != null
                ? `${t('Stop and save')} · ${Math.max(0, Math.round(at - marking))}s`
                : t('Mark a clip')}
            </button>
            {marking != null && (
              <span className="faint">{t('Press again where it stops.')}</span>
            )}
          </div>
        )}

        {pending && (
          <SaveMark
            t0={pending.t0}
            t1={pending.t1}
            saving={saving}
            error={saveError}
            onSave={(note) => void saveMark(note)}
            onCancel={() => { setPending(null); setSaveError(null) }}
          />
        )}
      </section>

      {/* ------------------------------------------------------- the archive
       *
       * On the page, always, with the day already drawn.
       *
       * The first build put this behind a text line reading "Nå CEST" that
       * opened a calendar when pressed. It was tidy and it was invisible:
       * nothing on the screen said the word recordings, nothing was shaped
       * like a thing you could press, and eighteen hours of stored audio sat
       * one unmarked click away. A farmer who did not already know the archive
       * existed had no way to find out.
       *
       * So the day is drawn from the first paint -- green where there is sound
       * -- with its own heading, a day either side of it, and a sentence
       * saying how much is there. The picture is the discovery. */}
      <section className="archive">
        <div className="arch-head">
          <h2 className="arch-title">{t('Listen back')}</h2>
          <button
            type="button"
            className="tzchip"
            aria-pressed={zone === 'utc'}
            onClick={() => setZone(zone === 'utc' ? 'local' : 'utc')}
            title={t('Show times in UTC')}
          >
            UTC
          </button>
        </div>

        <div className="arch-nav">
          <button type="button" className="arch-step"
            onClick={() => pickDay(shiftDay(viewDay, -1))}
            aria-label={t('Day before')}>‹</button>
          <button type="button" className="arch-day" aria-expanded={pickingDay}
            onClick={() => setPickingDay(!pickingDay)}>
            {isToday ? t('Today') : dayLabel(dayBounds(viewDay)[0])}
            <span className="faint"> {zoneLabel()}</span>
          </button>
          <button type="button" className="arch-step" disabled={isToday}
            onClick={() => pickDay(shiftDay(viewDay, 1))}
            aria-label={t('Day after')}>›</button>
        </div>

        {pickingDay && (
          <Kalender value={viewDay} max={todayKey} onPick={pickDay} />
        )}

        <DayStrip
          day={viewDay}
          hours={hours}
          clips={clips}
          /* At the live edge the playhead is the clock. Off it, it is wherever
             the archive player has reached. Same mark, one meaning: you are
             here. */
          at={live ? nowTick : at}
          onSeek={(e) => void seekArchive(e)}
          onOpenClip={openClip}
        />

        <p className="arch-say">
          {storedS > 0 ? (
            <>
              <strong className="num">{fmtSpan(t, storedS)}</strong>
              {' '}{t('of sound stored')}
              <span className="faint"> · {t('press anywhere to listen back')}</span>
            </>
          ) : (
            <span className="faint">{t('Nothing from this day has reached us yet.')}</span>
          )}
        </p>

        {lift > 0 && !live && (
          <p className="play-hint muted">
            {t('Lifted for listening')} +{Math.round(lift)} dB
          </p>
        )}

        {/* Under the day rather than beside it, and on every width.
            
            The aside carries this day's clips and exists only above 1100px;
            this is the farm's whole album and is the one place a phone can
            reach it. Below the strip because that is the reading order the
            page already teaches -- where sound is, then what was kept out of
            it. */}
        <ClipShelf
          clips={kept.data?.items ?? []}
          me={me}
          playing={focus?.clipId ?? null}
          onOpen={(c) => void openKept(c)}
          onChanged={() => {
            void kept.reload()
            void dayA.reload()
            void dayB.reload()
          }}
          onError={setArchiveError}
        />
      </section>
      </div>

      {/* The right-hand column, which used to be nine hundred pixels of empty
          green. Everything in it is already on the wire and nothing in it is
          inferred -- see the header of ListenAside for what that rules out and
          why the exclusions matter more than the inclusions. */}
      <ListenAside
        site={site.data}
        mission={active.find((m) => m.mission_id === missionId)}
        name={sensorName(
          active.find((m) => m.mission_id === missionId) ?? active[0]!,
          Math.max(0, active.findIndex((m) => m.mission_id === missionId)),
          t('Listening post'))}
        clips={clips}
        onOpenClip={openClip}
      />
    </div>
  )
}

/** A duration a person would say out loud. Whole hours above an hour, because
 *  "17,8 timer" is a measurement and this is a summary; minutes below it,
 *  because "0 timer" over eleven stored minutes would be a lie. */
function fmtSpan(t: (k: string) => string, s: number): string {
  // 'h' is the abbreviation, not the word: `18 t` in Norwegian, `18 h` in
  // English. Both are what a clock face uses.
  if (s >= 3600) return `${Math.round(s / 3600)} ${t('h')}`
  return `${Math.round(s / 60)} min`
}

/* Two glyphs, drawn rather than imported. A play triangle and a stop square are
 * the two most universally understood shapes in the product, and an icon set
 * would be a dependency for eleven path commands. */
function IconPlay() {
  return (
    <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  )
}

/* A band-pass response: a skirt up, a flat top, a skirt down. It is the shape
 * of what the two biquad filters underneath actually do to the sound, and it
 * is the shape every equaliser has drawn for fifty years -- which is the only
 * reason an unlabelled glyph is allowed to stand for a control at all. */
function IconFilter() {
  return (
    <svg viewBox="0 0 26 16" width="26" height="16" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.5 13.5C5 13.5 5 3 8.5 3h9c3.5 0 3.5 10.5 7 10.5" />
    </svg>
  )
}

function IconStop() {
  return (
    <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
      <rect x="7" y="7" width="10" height="10" rx="1" fill="currentColor" />
    </svg>
  )
}

/** WebKit on a phone, roughly. Used only to decide whether to mention the
 *  hardware silent switch, so a false positive costs one extra sentence and a
 *  false negative costs a support call. */
function isApple(): boolean {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/** What the node calls its second sensor, as a person would say it. */
function streamLabel(sensor: string | null, id: string): string {
  const s = (sensor ?? id).toLowerCase()
  if (s.includes('hydromoth') || s === 'moth') return 'HydroMoth'
  if (s.includes('audiomoth')) return 'AudioMoth'
  return sensor ?? id
}
